import React from 'react';
import { DeprecatedCollapse, ExternalLink, Modal } from '@common/components';
import { FontSize } from '@common/tokens';

type Props = {
  visible: boolean;
  onClose: () => void;
};

export function RolePermissionModal({ visible, onClose }: Props) {
  const { Panel } = DeprecatedCollapse;

  return (
    <>
      <Modal
        title="Role Permissions"
        visible={visible}
        onCancel={onClose}
        footer={
          <div className="member-form-role-modal-footer">
            For more information, see the{' '}
            <ExternalLink url="https://help.workshealth.com/en/articles/6797319-works-membership-levels">
              Membership Levels article
            </ExternalLink>
          </div>
        }
      >
        <DeprecatedCollapse textSize="lg">
          <Panel header="Organization and MSP Admin" key="1">
            <p>
              Organization and MSP Admins are system administrators, they are
              super-users that have full admin rights. They can take all actions
              that other roles can perform. Organization Admins can view
              invoices (along with Billing Managers). They are the only ones who
              can:
            </p>
            <ul>
              <li>Set up and update bill rates</li>
              <li>
                Bypass approval when creating a Staffing Request or extension
              </li>
              <li>Approve / reject items on behalf of designated approvers</li>
            </ul>
            <p>Note: Approver status and Reports are not role-dependent.</p>
          </Panel>
          <Panel header="Program Admin and Program Manager" key="2">
            <p>
              All Program Managers can shortlist submissions, and also take all
              actions available to Hiring Managers and Compliance Managers.
              Unlike Organization Admins, Program Managers cannot manage bill
              rates, bypass approvals, approve / reject items on behalf of
              approvers, or view invoices.
            </p>
            <p>
              There are two Program roles. Program Admin have access to some
              admin functionalities (set up templates, approval chains and
              memberships, and view rate cards), while Program Managers have no
              access to any admin functionalities.
            </p>
            <p>Note: Approver status and Reports are not role-dependent.</p>
          </Panel>
          <Panel header="Hiring Manager" key="3">
            <p>
              Hiring Managers are clinician managers overseeing a single unit or
              a group of units. They can create Staffing Requests, review
              shortlisted submissions, extend offers and extensions, view and
              create shift openings (if access granted), and verify time worked
              line item disputes.
            </p>
            <p>Note: Approver status and Reports are not role-dependent.</p>
          </Panel>
          <Panel header="Billing Manager" key="4">
            <p>
              Billing managers are responsible for financial operations. They
              can view invoices and verify line item disputes within the Billing
              section. They have view-only access to the Staffing and Team
              sections, and no access to other sections.
            </p>
            <p>Note: Approver status and Reports are not role-dependent.</p>
          </Panel>
          <Panel header="Compliance Manager" key="5">
            <p>
              Compliance managers are responsible for onboarding and ongoing
              compliance. They can set up compliance requirements, and verify
              compliance items. They have view-only access to the Staffing
              section and do not have access to the Candidate, Shifts, or
              Billing sections.
            </p>
            <p>Note: Approver status and Reports are not role-dependent.</p>
          </Panel>
          <Panel header="Executive and Read Only" key="6">
            <p>
              Executives and Read Only are users who need to sign onto Works to
              approve items or view reports, but do not use Works for other
              operational purposes. They have view-only access to the Staffing
              and Team sections, and no access to other sections. Executives,
              Read Only and all other roles can view reports if their account
              has been set up to access them.
            </p>
            <p>Note: Approver status and Reports are not role-dependent.</p>
          </Panel>
          <Panel header="Compliance Coordinator" key="7">
            <p>
              On-Demand Coordinators are support staff that perform tasks
              specific to shift management. They can manage on-demand staff and
              shift requests. They don’t have access to other sections.
            </p>
            <p>Note: Approver status and Reports are not role-dependent.</p>
          </Panel>
        </DeprecatedCollapse>
      </Modal>
      <style jsx>{`
        .member-form-role-modal-footer {
          padding: 14px 12px;
          font-size: ${FontSize.md};
          text-align: left;
        }
      `}</style>
    </>
  );
}
